import React from 'react';

import cs from 'classnames';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {DocumentMetadataStatusButton} from 'app/general/structures/DocumentMetadataStatusButton';
import {useUpdateProcedure} from 'app/procedures/api';
import {useUpdateProcess} from 'app/process/api';
import {gt} from 'app/utils/i18n';
interface TooltipProps {
  isSequenced: boolean;
  children: JSX.Element;
  contentType: 'procedure' | 'proceduregroup' | 'process';
}

function DocumentSequencedStatusTooltip({
  isSequenced,
  children,
  contentType,
}: TooltipProps): JSX.Element {
  return (
    <LazyTooltip
      tooltip={
        contentType === 'procedure'
          ? isSequenced
            ? gt(
                'Steps in this procedure have to be checked off in sequential order in task mode'
              )
            : gt(
                'Steps in this procedure can be checked off in any order in task mode'
              )
          : isSequenced
          ? gt(
              'Procedures in this process have to be checked off in sequential order in task mode'
            )
          : gt(
              'Procedures in this process can be checked off in any order in task mode'
            )
      }
    >
      {children}
    </LazyTooltip>
  );
}

interface ButtonProps {
  documentURL: string;
  isSequenced: boolean;
  canEdit: boolean;
  contentType: 'procedure' | 'proceduregroup';
}

export function DocumentSequencedStepsStatusButton(props: ButtonProps) {
  switch (props.contentType) {
    case 'procedure':
      return (
        <SequencedStepsStatusButton
          {...props}
          updateHook={useUpdateProcedure}
          inOrderMessage={gt('The steps must now be completed in order')}
          anyOrderMessage={gt('The steps can now be completed in any order')}
        />
      );

    case 'proceduregroup':
      return (
        <SequencedStepsStatusButton
          {...props}
          updateHook={useUpdateProcess}
          inOrderMessage={gt('The procedures must now be completed in order')}
          anyOrderMessage={gt(
            'The procedures can now be completed in any order'
          )}
        />
      );
  }
}

type SequencedProps = ButtonProps & {
  inOrderMessage: string;
  anyOrderMessage: string;
  updateHook: typeof useUpdateProcedure | typeof useUpdateProcess;
};

function SequencedStepsStatusButton({
  documentURL,
  isSequenced,
  canEdit,
  contentType,
  updateHook,
  inOrderMessage,
  anyOrderMessage,
}: SequencedProps) {
  const [update, {data, isLoading}] = updateHook();

  const localIsSequenced = data ? Boolean(data.steps_blocked_at) : isSequenced;

  function setSequenced() {
    const steps_blocked_at = localIsSequenced ? null : new Date().toISOString();
    update({
      url: documentURL,
      steps_blocked_at,
      successMessage: steps_blocked_at ? inOrderMessage : anyOrderMessage,
    });
  }

  return (
    <DocumentSequencedStatusTooltip
      contentType={contentType}
      isSequenced={localIsSequenced}
    >
      <DocumentMetadataStatusButton
        onClick={setSequenced}
        disabled={isLoading || !canEdit}
        active={localIsSequenced}
      >
        <i
          className={cs('fa-fw', {
            'fa fa-circle-notch fa-spin': isLoading,
            'fas fa-sort-numeric-down': !isLoading,
          })}
        />
      </DocumentMetadataStatusButton>
    </DocumentSequencedStatusTooltip>
  );
}

interface IconProps {
  isSequenced: boolean;
  className?: string;
  iconClassName?: string;
  contentType: 'procedure' | 'proceduregroup' | 'process';
}
export function DocumentSequencedStepsStatusIcon({
  className,
  iconClassName,
  isSequenced,
  contentType,
}: IconProps): JSX.Element | null {
  return isSequenced ? (
    <DocumentSequencedStatusTooltip
      isSequenced={status != null}
      contentType={contentType}
    >
      <span className={cs(className)}>
        <i className={cs('fa fa-link', iconClassName)} />
      </span>
    </DocumentSequencedStatusTooltip>
  ) : null;
}
